import React, { useState, useEffect } from "react";
import {
  Clock,
  MessageSquare,
  Users,
  UserPlus,
  Settings,
  FileText,
  Award,
  Zap,
  Calendar,
  Activity,
  TrendingUp,
  BookOpen,
} from "lucide-react";
import communityService from "../../services/communityService";
import "./styles/index.css";

const CommunityTimeline = ({ communityId, detailed = false, activities, analytics }) => {
  const [timelineActivities, setTimelineActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
  }, [communityId, activities]);

  const fetchActivities = async () => {
    try {
      setLoading(true);

      // Use activities from preview data if available
      if (activities && activities.length > 0) {
        setTimelineActivities(mapActivitiesToTimelineFormat(activities));
        setLoading(false);
        return;
      }

      // Fallback: try to fetch activities directly
      try {
        const response = await communityService.getCommunityActivity(
          communityId
        );
        if (response.success && response.data) {
          setTimelineActivities(
            mapActivitiesToTimelineFormat(response.data.activities || [])
          );
        } else {
          // No data available - show empty state (no mock data!)
          setTimelineActivities([]);
        }
      } catch (error) {
// Show empty state instead of fake mock data
        setTimelineActivities([]);
      }
    } catch (error) {
setTimelineActivities([]);
    } finally {
      setLoading(false);
    }
  };

  // Map API activity format to component format
  const mapActivitiesToTimelineFormat = (apiActivities) => {
    return apiActivities.map((activity, index) => {
      const getActivityIcon = (type) => {
        switch (type) {
          case "member_joined":
            return UserPlus;
          case "thread_created":
            return MessageSquare;
          case "room_created":
            return BookOpen;
          case "milestone":
            return Award;
          default:
            return Activity;
        }
      };

      const getActivityColor = (type) => {
        switch (type) {
          case "member_joined":
            return "#4caf50";
          case "thread_created":
            return "#2196f3";
          case "room_created":
            return "#ff9800";
          case "milestone":
            return "#9c27b0";
          default:
            return "#757575";
        }
      };

      return {
        id: index + 1,
        type: activity.type,
        title: getActivityTitle(activity),
        description: activity.description,
        user: activity.user || {
          name: "Community System",
          avatar: null,
          role: "system",
        },
        timestamp: new Date(activity.timestamp),
        icon: getActivityIcon(activity.type),
        color: getActivityColor(activity.type),
        metadata: {
          threadId: activity.threadId,
          threadTitle: activity.threadTitle,
          milestone: activity.milestone,
        },
      };
    });
  };

  const getActivityTitle = (activity) => {
    switch (activity.type) {
      case "member_joined":
        return "New Member Joined";
      case "thread_created":
        return "New Discussion Started";
      case "room_created":
        return "New Research Room Created";
      case "milestone":
        return "Community Milestone";
      default:
        return "Community Activity";
    }
  };



  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diffMs = now - timestamp;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else {
      return `${diffDays}d ago`;
    }
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      creator: "Creator",
      moderator: "Moderator",
      member: "Member",
      system: "System",
    };
    return roleMap[role] || "Member";
  };

  if (loading) {
    return (
      <div className="community-preview-content-card">
        <h3>
          <Clock size={20} />
          Recent Activity
        </h3>
        <div className="community-preview-timeline-loading">
          <div className="loading-spinner"></div>
          <p>Loading activity...</p>
        </div>
      </div>
    );
  }

  const displayActivities = detailed
    ? timelineActivities
    : timelineActivities.slice(0, 5);

  return (
    <div className="community-timeline">
      <div className="community-preview-content-card">
        <h3>
          <Clock size={20} />
          {detailed ? "Complete Activity Timeline" : "Recent Activity"}
        </h3>

        <div className="community-preview-timeline-container">
          {displayActivities.map((activity, index) => (
            <div key={activity.id} className="community-preview-timeline-item">
              <div className="community-preview-timeline-marker">
                <div
                  className="community-preview-timeline-icon"
                  style={{
                    backgroundColor: `${activity.color}15`,
                    color: activity.color,
                  }}
                >
                  <activity.icon size={16} />
                </div>
                {index < displayActivities.length - 1 && (
                  <div className="community-preview-timeline-line"></div>
                )}
              </div>

              <div className="community-preview-timeline-content">
                <div className="community-preview-timeline-header">
                  <h4 className="community-preview-timeline-title">
                    {activity.title}
                  </h4>
                  <span className="community-preview-timeline-time">
                    {formatTimeAgo(activity.timestamp)}
                  </span>
                </div>

                <p className="community-preview-timeline-description">
                  {activity.description}
                </p>

                {activity.user.name !== "Community System" && (
                  <div className="community-preview-timeline-user">
                    <div className="community-preview-user-avatar">
                      {activity.user.avatar ? (
                        <img
                          src={activity.user.avatar}
                          alt={activity.user.name}
                        />
                      ) : (
                        <Users size={16} />
                      )}
                    </div>
                    <span className="community-preview-user-name">
                      {activity.user.name}
                    </span>
                    <span
                      className="community-preview-user-role-badge"
                      data-role={activity.user.role}
                    >
                      {getRoleDisplayName(activity.user.role)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {!detailed && timelineActivities.length > 5 && (
          <div className="community-preview-timeline-footer">
            <button className="community-preview-view-more-btn">
              <Activity size={16} />
              View Complete Timeline
            </button>
          </div>
        )}
      </div>

      {detailed && analytics && (
        <div className="community-preview-content-card">
          <h3>
            <TrendingUp size={20} />
            Activity Summary
          </h3>

          <div className="community-preview-activity-summary">
            <div className="community-preview-summary-stats">
              <div className="community-preview-summary-stat">
                <div className="community-preview-stat-number">
                  {analytics.memberEngagement?.totalActivity || timelineActivities.length || 0}
                </div>
                <div className="stat-label">Activities this week</div>
              </div>
              <div className="community-preview-summary-stat">
                <div className="community-preview-stat-number">
                  {analytics.totalMembers || 0}
                </div>
                <div className="stat-label">Total members</div>
              </div>
              <div className="community-preview-summary-stat">
                <div className="community-preview-stat-number">
                  {analytics.engagementRate?.percentage || analytics.memberEngagement?.rate || 0}%
                </div>
                <div className="stat-label">Members engaged</div>
              </div>
            </div>

            <div className="community-preview-activity-types">
              <div className="community-preview-activity-type">
                <MessageSquare size={16} /> Active Discussions: {analytics.activeDiscussions || 0}
              </div>
              <div className="community-preview-activity-type">
                <UserPlus size={16} /> New Members: {analytics.memberGrowth?.newMembersThisMonth || 0}
              </div>
              <div className="community-preview-activity-type">
                <Activity size={16} /> Daily Active: {analytics.dailyActiveUsers?.count || 0}
              </div>
              <div className="community-preview-activity-type">
                <Clock size={16} /> Avg Response: {analytics.responseTime?.hours || 0}h
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityTimeline;
